/**
 * DocPermissionService — Handles access revocation UI and navigation.
 *
 * sl-jsx3.14.3: Subscribes to AccessibleDocCacheStore.revokedDocIds$ and reacts
 * when the user's access to a document is revoked:
 * - Shows a toast notification
 * - Navigates away from the revoked doc if it's currently open
 *
 * Server is the single source of truth — no conflict resolution.
 */
import { toast } from '@affine/component';
import { I18n } from '@affine/i18n';
import { Service } from '@toeverything/infra';

import type { GuardService } from '../../permissions/services/guard';
import type { WorkbenchService } from '../../workbench';
import type { AccessibleDocCacheStore } from '../stores/accessible-doc-cache';

export class DocPermissionService extends Service {
  constructor(
    private readonly accessibleDocCache: AccessibleDocCacheStore,
    private readonly workbenchService: WorkbenchService,
    private readonly guardService: GuardService
  ) {
    super();
    this.watchRevocations();
  }

  /**
   * sl-jsx3.17.2: Indicates the permission cache is from IDB only (not yet server-confirmed).
   */
  readonly isStale$ = this.accessibleDocCache.isStale$;

  /**
   * Subscribe to revoked doc IDs and handle each revocation event.
   * sl-jsx3.17.2: Also triggers GuardService revalidation so the
   * detail-page's canAccess check updates after revocation.
   */
  private watchRevocations(): void {
    const sub = this.accessibleDocCache.revokedDocIds$.subscribe(
      (revokedIds: string[]) => {
        if (revokedIds.length === 0) return;

        const currentDocId = this.getCurrentDocId();
        const isCurrentDocRevoked =
          currentDocId !== null && revokedIds.includes(currentDocId);

        if (isCurrentDocRevoked) {
          toast(I18n['com.streamlain.notification.doc-role-revoked']());
          // Trigger GuardService revalidation for the revoked doc
          this.guardService.revalidateCan('Doc_Read', currentDocId);
          this.workbenchService.workbench.openAll();
        }

        // Reset after handling (one-shot event)
        this.accessibleDocCache.revokedDocIds$.setValue([]);
      }
    );
    this.disposables.push(() => sub.unsubscribe());
  }

  /**
   * Extract the current document ID from the workbench location.
   * Returns null if not viewing a document (e.g. on /all, /trash, etc.).
   */
  private getCurrentDocId(): string | null {
    const location = this.workbenchService.workbench.location$.value;
    if (!location) return null;

    const pathname = location.pathname;
    // Doc URLs are like /{docId} — a single path segment (UUID-like)
    // Non-doc URLs: /all, /trash, /collection/xxx, /tag/xxx
    const match = pathname.match(/^\/([a-zA-Z0-9_-]+)$/);
    if (!match) return null;

    const segment = match[1];
    // Exclude known non-doc routes
    const nonDocRoutes = new Set([
      'all',
      'trash',
      'collection',
      'tag',
      'search',
    ]);
    if (nonDocRoutes.has(segment)) return null;

    return segment;
  }
}
